// The link gate, re-send warning, per-field outcomes and post-send guidance all live in the shared
// JdOpsSend block, so this surface and the editor's cannot drift apart.
import { useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { buildSpreadZip } from "../../lib/spreadExport";
import { JdOpsSend } from "../../components/JdOpsSend";
import type { SpreadRunResult } from "../../lib/spreadRun";
import type { DashboardField } from "../../types/contract";
import { Modal } from "../../ui/Modal";

export function SpreadDeliverModal({
  title,
  fields,
  result,
  growerName,
  ourProduct,
  busy,
  error,
  onClose,
}: {
  title: string;
  fields: DashboardField[];
  result: SpreadRunResult | null;
  growerName: string;
  /* * what's being spread — the product-mapping vocabulary word (the preset's label) */
  ourProduct: string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
}) {
  const ready = (result?.exports.length ?? 0) > 0;
  // the send loop must never keep uploading behind a CLOSED surface (pre-ship review): every close
  // route locks while JdOpsSend reports sending, and JdOpsSend's own unmount belt is only the
  // backstop for paths this lock can't reach.
  const [sendBusy, setSendBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const locked = busy || sendBusy || downloading;

  const download = async () => {
    if (!result || !ready || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const { name, data } = await buildSpreadZip({
        fields: result.exports,
        growerName,
        now: new Date(),
      });
      const url = URL.createObjectURL(
        new Blob([data as unknown as BlobPart], { type: "application/zip" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  // the send lane addresses fields by ID — exports without one (never expected: both producers stamp
  // it) simply don't reach the send block rather than being matched by name
  const sendable = (result?.exports ?? []).flatMap((e) =>
    e.fieldId ? [{ ...e, fieldId: e.fieldId }] : [],
  );

  return (
    <Modal>
      <div
        className="fixed inset-0 z-dialog flex items-center justify-center bg-black/40 p-4"
        onClick={() => !locked && onClose()}
      >
        <div
          className="flex w-full max-w-sm flex-col rounded-lg bg-surface text-fg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <h2 className="truncate text-sm font-semibold">{title}</h2>
            <button
              onClick={onClose}
              disabled={locked}
              className="rounded p-1 hover:bg-hover disabled:opacity-50"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div
            className="flex flex-col gap-2 overflow-auto p-4 text-[12px]"
            style={{ maxHeight: "min(560px, 70vh)" }}
          >
            {busy && (
              <p className="flex items-center gap-1.5 text-muted">
                <Loader2 size={12} className="animate-spin text-accent" />{" "}
                Building the spread map…
              </p>
            )}
            {error && <p className="text-red-400">{error}</p>}
            {downloadError && <p className="text-red-400">{downloadError}</p>}
            {!busy && !error && (
              <p className="text-muted">
                {ready
                  ? `Ready for ${result!.exports.length} field${result!.exports.length === 1 ? "" : "s"} — column ${result!.column}.`
                  : "Nothing to spread."}
              </p>
            )}

            {/*
 Fields that produced nothing are NAMED. A field quietly missing from a spread file is
              worse than being told which one and why.
*/}
            {!busy && (result?.notices?.length ?? 0) > 0 && (
              <ul className="ml-3 list-disc text-[11px] text-muted">
                {result!.notices.map((note) => (
                  <li key={`${note.fieldName}:${note.message}`}>
                    {note.fieldName} — {note.message}
                  </li>
                ))}
              </ul>
            )}
            {!busy && (result?.skipped.length ?? 0) > 0 && (
              <ul className="ml-3 list-disc text-[11px] text-faint">
                {result!.skipped.map((s) => (
                  <li key={s.fieldName}>
                    {s.fieldName} — {s.reason}
                  </li>
                ))}
              </ul>
            )}

            {/* THE JD LANE — gate, re-send warning, send, per-field outcomes, work-plan guidance. */}
            {!busy && (
              <JdOpsSend
                fields={fields}
                exports={sendable}
                growerName={growerName}
                ourProduct={ourProduct}
                disabled={busy || !ready}
                onSendingChange={setSendBusy}
              />
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-3 py-2">
            <button
              onClick={() => void download()}
              disabled={locked || !ready}
              className="flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-[12px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-40"
            >
              <Download size={13} /> Download
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
